import { createContext, useContext, useState, useCallback } from 'react';

const AdminAuthContext = createContext(null);

/**
 * Centralized admin authentication provider.
 * Stores token in memory (React state) instead of localStorage.
 * Uses sessionStorage as fallback for page refresh persistence
 * (safer than localStorage: cleared when browser tab closes).
 */
export function AdminAuthProvider({ children }) {
  const [token, setTokenState] = useState(() => {
    return sessionStorage.getItem('admin_token') || null;
  });

  const setToken = useCallback((newToken) => {
    setTokenState(newToken);
    if (newToken) {
      sessionStorage.setItem('admin_token', newToken);
    } else {
      sessionStorage.removeItem('admin_token');
    }
  }, []);

  const clearToken = useCallback(() => {
    setTokenState(null);
    sessionStorage.removeItem('admin_token');
  }, []);

  return (
    <AdminAuthContext.Provider value={{ token, setToken, clearToken }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

/**
 * Hook to access admin auth context.
 * Returns { token, setToken, clearToken }
 */
export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    // Fallback for components not wrapped in AdminAuthProvider
    return {
      token: sessionStorage.getItem('admin_token'),
      setToken: (t) => {
        if (t) sessionStorage.setItem('admin_token', t);
        else sessionStorage.removeItem('admin_token');
      },
      clearToken: () => sessionStorage.removeItem('admin_token'),
    };
  }
  return context;
}

// Legacy helper functions for backward compatibility during migration
export const getAdminToken = () => sessionStorage.getItem('admin_token');
export const setAdminToken = (token) => {
  if (token) sessionStorage.setItem('admin_token', token);
  else sessionStorage.removeItem('admin_token');
};
export const clearAdminToken = () => sessionStorage.removeItem('admin_token');
